import { useCallback, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import cn from 'bem-cn';

import { useGetChallengeQuery, useGetCoursesByIdsQuery } from '../../store';
import { WebAppLayout } from '../../layouts/WebAppLayout';
import { HeaderElements } from '../../components/Header';
import { NavigationElement, NavigationElements } from '../../components/Navigation';
import { Stack } from '../../components/UI';
import { useNavigateBack, useSlug, useWithAuth } from '../../hooks';
import { ChallengeInfo } from '../../components/ChallengeInfo';
import { isError404 } from '../../utils/error/isError';
import { RoutePaths } from '../../constants';
import { BackButton } from '../../components/BackButton';
import { getDaysUntil } from '../../utils/date/date';

import './ChallengePage.scss';

const b = cn('challenge-page');

export const ChallengePage = () => {
    const { slug: challengeId, isValidSlug: isValidChallengeId } = useSlug();
    const { withAuth } = useWithAuth();

    const { data, isFetching, error } = useGetChallengeQuery({ challengeId });
    const { data: relatedCourses } = useGetCoursesByIdsQuery(
        { ids: data?.relatedCourseIds || [] },
        { skip: !data?.relatedCourseIds?.length },
    );

    const handleBackButtonClick = useNavigateBack();

    const headerElements: HeaderElements = useMemo(
        () => ({
            desktop: {
                left: { type: 'logo' },
                right: { type: 'profile' },
            },
            mobile: {
                left: { type: 'backButton', onClick: handleBackButtonClick },
                right: { type: 'profile' },
            },
        }),
        [handleBackButtonClick],
    );

    const daysLeft = getDaysUntil(data?.deadline);

    const openForm = useCallback(() => {
        if (daysLeft >= 0 && data?.formUrl) {
            window.open(data.formUrl, '_blank', 'noopener, noreferrer');
        }
    }, [data?.formUrl, daysLeft]);

    const handleFormButtonClick = useCallback(() => {
        withAuth(openForm, {
            retpath: encodeURIComponent(window.location.href),
        });
    }, [openForm, withAuth]);

    const formLinkButton: NavigationElement = useMemo(() => {
        return {
            text: isFetching || daysLeft >= 0 ? 'Принимаю челлендж!' : 'Приём заявок завершён',
            accent: true,
            onClick: handleFormButtonClick,
        };
    }, [daysLeft, handleFormButtonClick, isFetching]);

    const navigationElements: NavigationElements = useMemo(() => {
        return {
            desktop: [formLinkButton],
            mobile: [formLinkButton],
        };
    }, [formLinkButton]);

    if (!isValidChallengeId || isError404(error)) {
        return <Navigate to={RoutePaths.NotFound} replace />;
    }

    return (
        <WebAppLayout headerElements={headerElements} navigationElements={navigationElements}>
            <Stack className={b()}>
                {isFetching && !data && (
                    <div style={{ textAlign: 'center', marginTop: '50vh', transform: 'translateY(-50%)' }}>
                        Загрузка...
                    </div>
                )}
                {data && <ChallengeInfo challenge={data} courses={relatedCourses ?? []} />}
                <BackButton className={b('back-button')} />
            </Stack>
        </WebAppLayout>
    );
};
